import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 编程教程动画 - 代码讲解
 * 
 * 核心效果：
 * 1. 代码逐行高亮
 * 2. 输出结果展示
 * 3. 步骤说明
 * 4. 关键点标注
 */

export const CodingTutorialAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const codeLines = [
    'function fibonacci(n) {',
    '  if (n <= 1) return n;',
    '  ',
    '  let a = 0, b = 1;',
    '  for (let i = 2; i <= n; i++) {',
    '    let temp = a + b;',
    '    a = b;',
    '    b = temp;',
    '  }',
    '  return b;',
    '}',
    '',
    '// Example usage',
    'console.log(fibonacci(10));',
  ];

  const currentLine = Math.floor(interpolate(frame, [0, 180], [0, codeLines.length], { extrapolateRight: 'clamp' }));
  
  const outputOpacity = interpolate(frame, [200, 230], [0, 1], { extrapolateRight: 'clamp' });
  const outputScale = spring({
    frame: frame - 200,
    from: 0.8,
    to: 1,
    fps,
    config: { damping: 15, stiffness: 100 },
  });

  const steps = [
    { icon: '1️⃣', text: '初始化变量', delay: 240 },
    { icon: '2️⃣', text: '循环计算', delay: 270 },
    { icon: '3️⃣', text: '返回结果', delay: 300 },
  ];

  return (
    <AbsoluteFill style={{
      background: '#1e1e1e',
      fontFamily: 'Consolas, Monaco, monospace',
      color: '#d4d4d4',
      padding: 60,
    }}>
      {/* 标题 */}
      <div style={{
        position: 'absolute',
        top: 40,
        left: 0,
        right: 0,
        textAlign: 'center',
      }}>
        <h1 style={{
          fontSize: 60,
          fontWeight: 700,
          color: '#569cd6',
          margin: 0,
        }}>
          斐波那契数列 - JavaScript
        </h1>
        <p style={{
          fontSize: 30,
          color: '#9cdcfe',
          marginTop: 10,
        }}>
          Fibonacci Sequence Tutorial
        </p>
      </div>

      {/* 代码区域 */}
      <div style={{
        position: 'absolute',
        top: 280,
        left: 60,
        right: '50%',
        bottom: 100,
        background: '#252526',
        borderRadius: 15,
        padding: 40,
        overflow: 'hidden',
        border: '2px solid #3c3c3c',
      }}>
        {/* 行号 */}
        <div style={{
          position: 'absolute',
          left: 20,
          top: 40,
          color: '#858585',
          fontSize: 28,
          lineHeight: '40px',
          userSelect: 'none',
        }}>
          {codeLines.map((_, i) => (
            <div key={i}>{i + 1}</div>
          ))}
        </div>

        {/* 代码内容 */}
        <div style={{
          marginLeft: 60,
          fontSize: 28,
          lineHeight: '40px',
        }}>
          {codeLines.map((line, i) => {
            const isCurrentLine = i === currentLine;
            const isPastLine = i < currentLine;
            
            return (
              <div key={i} style={{
                padding: '2px 10px',
                background: isCurrentLine ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                borderLeft: isCurrentLine ? '4px solid #569cd6' : '4px solid transparent',
                opacity: isPastLine || isCurrentLine ? 1 : 0.3,
              }}>
                {line || ' '}
              </div>
            );
          })}
        </div>
      </div>

      {/* 输出区域 */}
      <div style={{
        position: 'absolute',
        top: 280,
        left: '52%',
        right: 60,
        bottom: 100,
        display: 'flex',
        flexDirection: 'column',
        gap: 30,
      }}>
        {/* 输出标题 */}
        <div style={{
          fontSize: 36,
          color: '#4ec9b0',
          fontWeight: 700,
        }}>
          💻 输出结果
        </div>

        {/* 输出内容 */}
        <div style={{
          background: '#1e1e1e',
          borderRadius: 15,
          padding: 30,
          border: '2px solid #3c3c3c',
          opacity: outputOpacity,
          transform: `scale(${outputScale})`,
        }}>
          <div style={{
            fontSize: 32,
            color: '#ce9178',
            marginBottom: 20,
          }}>
            {'>'} fibonacci(10)
          </div>
          <div style={{
            fontSize: 48,
            color: '#b5cea8',
            fontWeight: 700,
          }}>
            55
          </div>
          <div style={{
            fontSize: 26,
            color: '#9cdcfe',
            marginTop: 20,
          }}>
            ✅ 第 10 个斐波那契数
          </div>
        </div>

        {/* 步骤说明 */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}>
          {steps.map((step, i) => {
            const stepOpacity = interpolate(
              frame,
              [step.delay, step.delay + 20],
              [0, 1],
              { extrapolateRight: 'clamp' }
            );

            return (
              <div key={i} style={{
                padding: '20px 30px',
                background: 'rgba(86, 156, 214, 0.2)',
                borderRadius: 12,
                border: '2px solid rgba(86, 156, 214, 0.4)',
                opacity: stepOpacity,
                display: 'flex',
                alignItems: 'center',
                gap: 15,
              }}>
                <span style={{ fontSize: 34 }}>{step.icon}</span>
                <span style={{ fontSize: 30 }}>{step.text}</span>
              </div>
            );
          })}
        </div>
      </div>
    </AbsoluteFill>
  );
};
